import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { NUM, TRY, unitLabel } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { invalidateProductIndex } from "@/lib/productIndex";
import { confirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Boxes, Warehouse, Store, Wallet, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PortalToolbar } from "./PortalToolbar";
import { PortalTable } from "./PortalTable";
import { PortalBoard } from "./PortalBoard";
import { ProductDrawer } from "./ProductDrawer";
import { BulkBar, BulkMoveDialog, BulkEditDialog } from "./BulkBar";
import { BulkZeroDialog } from "./BulkZeroDialog";
import { patchField, setLocQty, transfer, bulkTransfer, applyMove, undoToast, ROUTE } from "./portalApi";

const DEF = { q: "", brand: "", series: [], category: "", discount_group: "", kind: "", stock: "" };

function Summary({ s }) {
  const cards = [
    ["Ürün", NUM(s.count), Boxes, "bg-[#0F2745]/10 text-[#0F2745]", "portal-sum-count"],
    ["Depo", NUM(s.depo), Warehouse, "bg-slate-100 text-slate-600", "portal-sum-depo"],
    ["Mağaza", NUM(s.magaza), Store, "bg-teal-50 text-teal-700", "portal-sum-magaza"],
    ["Stok değeri", TRY(s.value), Wallet, "bg-amber-50 text-amber-700", "portal-sum-value"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map(([l, v, Icon, cls, t]) => (
        <div key={l} className="bg-white p-3 rounded-xl border border-slate-200 flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cls}`}><Icon className="h-5 w-5" /></div>
          <div className="min-w-0"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{l}</div><div className="font-head font-black text-base sm:text-lg text-[#0F2745] tnum truncate" title={v} data-testid={t}>{v}</div></div>
        </div>
      ))}
    </div>
  );
}

function Pager({ data, page, setPage, ps, setPs }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap text-sm text-slate-500" data-testid="portal-pager">
      <span><b className="text-slate-700 tnum">{NUM(data.total)}</b> ürün · sayfa <b className="tnum">{page}</b>/<span className="tnum">{data.pages}</span></span>
      <div className="flex items-center gap-2">
        <Select value={String(ps)} onValueChange={(v) => setPs(Number(v))}>
          <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="portal-page-size"><SelectValue /></SelectTrigger>
          <SelectContent>{[50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n} / sayfa</SelectItem>)}</SelectContent>
        </Select>
        <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="portal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="portal-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

/** Ürün & Stok Portalı — filtrelenebilir/sıralanabilir tablo, anında hücre düzenleme, Depo↔Mağaza kaydırıcı/pano, toplu işlemler. */
export default function StockPortal({ onOpenEdit, reloadKey }) {
  const [f, setF] = useState(DEF);
  const [qIn, setQIn] = useState("");
  const [sort, setSortS] = useState({ key: "value", dir: "desc" });
  const [page, setPage] = useState(1);
  const [ps, setPsS] = useState(50);
  const [view, setView] = useState("table");
  const [data, setData] = useState({ items: [], total: 0, pages: 1, summary: {} });
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({});
  const [selected, setSelected] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [moveTo, setMoveTo] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [zeroOpen, setZeroOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const req = useRef(0);

  const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage(1); setSelected([]); };
  const setSort = (s) => { setSortS(s); setPage(1); };
  const setPs = (n) => { setPsS(n); setPage(1); };
  const params = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { (Array.isArray(v) ? v : [v]).forEach((x) => { if (x) p.append(k, x); }); });
    return p;
  };
  const load = async (silent = false) => {
    const id = ++req.current;
    if (!silent) setLoading(true);
    try {
      const p = params();
      p.append("sort", sort.key); p.append("dir", sort.dir); p.append("page", page); p.append("page_size", ps);
      const { data: d } = await api.get(`/products/portal?${p}`);
      if (id !== req.current) return;
      setData(d);
      setDrawer((cur) => (cur ? d.items.find((x) => x.id === cur.id) || cur : cur));
    } catch (e) { if (!silent) toast.error(apiError(e)); }
    finally { if (id === req.current) setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => { if (qIn !== f.q) setFilter({ q: qIn }); }, 350); return () => clearTimeout(t); }, [qIn]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [f, sort, page, ps, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/products/facets").then((r) => setFacets(r.data || {})).catch(() => {}); }, []);
  useAutoRefresh(() => load(true), { interval: 20000 });

  const updateRow = (row) => {
    setData((d) => ({ ...d, items: d.items.map((x) => (x.id === row.id ? { ...x, ...row } : x)) }));
    setDrawer((cur) => (cur && cur.id === row.id ? { ...cur, ...row } : cur));
  };
  const reload = () => load(true);
  const h = {
    saveField: async (p, field, value) => { updateRow(await patchField(p.id, field, value)); invalidateProductIndex(); },
    saveLoc: async (p, loc, v) => {
      const q = Number(String(v).replace(",", "."));
      if (!Number.isFinite(q) || String(v).trim() === "") throw new Error("Geçerli bir sayı girin");
      const old = loc === "magaza" ? p.qty_magaza : p.qty_depo;
      const m = await setLocQty(p, loc, q);
      updateRow(applyMove(p, m)); invalidateProductIndex(); reload();
      undoToast(`${p.name} · ${loc === "magaza" ? "Mağaza" : "Depo"} ${NUM(old)} → ${NUM(q)}`, [m.id], reload);
    },
    move: async (p, to, q) => {
      try {
        const m = await transfer(p, to, q);
        updateRow(applyMove(p, m)); invalidateProductIndex(); reload();
        undoToast(`${p.name} · ${NUM(q)} ${unitLabel(p.unit)} ${ROUTE[to]}`, [m.id], reload);
      } catch (e) { toast.error(apiError(e)); throw e; }
    },
    openDrawer: (p) => setDrawer(p),
  };
  const runBulkMove = async (ids, to, opts) => {
    setBusy(true);
    try {
      const r = await bulkTransfer(ids, to, opts);
      setMoveTo(null); invalidateProductIndex(); reload();
      if (!r.moved) toast.info(`Seçili ürünlerde ${to === "magaza" ? "depoda" : "mağazada"} taşınacak stok yok`);
      else undoToast(`${r.moved} ürün ${ROUTE[r.to]} taşındı · ${NUM(r.total_qty)} birim${r.skipped ? ` · ${r.skipped} üründe stok yok` : ""}`, r.movement_ids, reload);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const moveIds = async (ids, to) => {
    if (!await confirm(`${ids.length} üründe ${to === "magaza" ? "Depo'daki" : "Mağaza'daki"} stoğun tamamı ${to === "magaza" ? "Mağaza'ya" : "Depo'ya"} taşınacak.`, { title: ROUTE[to], confirmText: "Taşı" })) return;
    runBulkMove(ids, to, { mode: "all" });
  };
  const runBulkEdit = async (field, value) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/products/portal/bulk-set", { ids: selected, field, value });
      toast.success(`${r.count} üründe güncellendi`); setEditOpen(false); invalidateProductIndex(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const selectAllFilter = async () => {
    try {
      const { data: r } = await api.get(`/products/portal/ids?${params()}`);
      setSelected(r.ids);
      if (r.truncated) toast.warning("Filtrede 5000'den fazla ürün var — ilk 5000 seçildi");
    } catch (e) { toast.error(apiError(e)); }
  };
  const zeroDone = (r) => {
    setZeroOpen(false); invalidateProductIndex(); reload();
    undoToast(`${r.products} üründe stok sıfırlandı · hareket geçmişi korundu`, r.movement_ids, reload);
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePage = (on) => setSelected((s) => (on ? [...new Set([...s, ...data.items.map((p) => p.id)])] : s.filter((id) => !data.items.some((p) => p.id === id))));
  const pageAll = data.items.length > 0 && data.items.every((p) => selected.includes(p.id));

  return (
    <div className="space-y-3 pb-20" data-testid="stock-portal">
      <PortalToolbar qIn={qIn} setQIn={setQIn} f={f} setFilter={setFilter} facets={facets} sort={sort} setSort={setSort} view={view} setView={setView} onSelectFilter={selectAllFilter}
        onReset={() => { setQIn(""); setF(DEF); setPage(1); setSelected([]); }} />
      <Summary s={data.summary || {}} />
      {(pageAll || selected.length > 0) && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-center gap-2 flex-wrap" data-testid="portal-select-banner">
          <span><b className="tnum">{NUM(selected.length)}</b> ürün seçili.</span>
          {selected.length < data.total && <button className="font-bold underline underline-offset-2" onClick={selectAllFilter} data-testid="portal-select-all-filter">Filtredeki tüm {NUM(data.total)} ürünü seç</button>}
          <button className="ml-auto text-amber-700 hover:text-amber-900" onClick={() => setSelected([])} data-testid="portal-select-clear">Seçimi temizle</button>
        </div>
      )}
      {view === "table"
        ? <PortalTable items={data.items} loading={loading} sort={sort} onSort={(k) => setSort({ key: k, dir: sort.key === k && sort.dir === "asc" ? "desc" : "asc" })} selected={selected} onToggle={toggle} onTogglePage={togglePage} h={h} />
        : <PortalBoard items={data.items} selected={selected} onMoveOne={(p, to, q) => h.move(p, to, q).catch(() => {})} onMoveIds={moveIds} />}
      <Pager data={data} page={page} setPage={setPage} ps={ps} setPs={setPs} />
      <BulkBar count={selected.length} onMove={setMoveTo} onEdit={() => setEditOpen(true)} onZero={() => setZeroOpen(true)} onClear={() => setSelected([])} />
      <BulkZeroDialog open={zeroOpen} ids={selected} onCancel={() => setZeroOpen(false)} onDone={zeroDone} />
      {moveTo && <BulkMoveDialog to={moveTo} count={selected.length} busy={busy} onCancel={() => setMoveTo(null)} onConfirm={(opts) => runBulkMove(selected, moveTo, opts)} />}
      {editOpen && <BulkEditDialog open={editOpen} count={selected.length} busy={busy} facets={facets} onCancel={() => setEditOpen(false)} onConfirm={runBulkEdit} />}
      <ProductDrawer p={drawer} onClose={() => setDrawer(null)} h={h} onOpenEdit={(p) => { setDrawer(null); onOpenEdit(p); }} />
    </div>
  );
}
